import React, { HTMLProps, ReactNode } from 'react'

import { IconButton, TextFieldProps } from '@mui/material'
import { Col, Span } from 'components'
import { Controller } from 'react-hook-form'
import MaskInput from 'react-input-mask'
import styled, { css } from 'styled-components'
import { COLORS, SPACING } from 'utils'

import { InputCustom, InputLabelCustom } from './InputMask.styles'

type Masks =
  | 'cnpj'
  | 'cpf'
  | 'cep'
  | 'days'
  | 'phone'
  | 'cellphone'
  | 'rg'
  | 'currency'
  | 'percentage'
  | 'process'
  | 'passport'

interface IInputMask extends HTMLProps<TextFieldProps> {
  control?: any
  formValue?: string
  defaultValue?: string | number
  masks?: Masks
  mask?: string
  required?: boolean
  label?: string
  inputIcon?: ReactNode
  onClickIcon?: () => void
  marginTop?: string
  marginBottom?: string
  marginRight?: string
  marginLeft?: string
  errors?: any
  maskChar?: string | null
  type?: string
  value?: string | number
  onChange?: any
  onBlur?: (e: any) => void
  width?: string
  customError?: any
}

const StyledMaskInput = styled(MaskInput) <IInputMask>`
  ${({ width }) => {
    return css`
      width: ${width};
    `
  }}
`

const InputMask: React.FC<IInputMask> = ({
  control,
  errors,
  customError,
  marginBottom,
  marginRight,
  marginTop,
  marginLeft,
  formValue,
  defaultValue,
  masks = null,
  mask = null,
  required,
  label = '',
  inputIcon,
  onClickIcon,
  maskChar = '_',
  maxLength = 100,
  type = 'all',
  value,
  onChange,
  onBlur,
  width,
  ...props
}) => {
  const formatLabel = () => label.charAt(0).toUpperCase() + label.slice(1)
  const hasError = (errors && errors[formValue]) || customError

  const mascaras = {
    cellphone: '(99) 9 9999-9999',
    cep: '99999-999',
    cnpj: '99.999.999/9999-99',
    cpf: '999.999.999-99',
    days: '999',
    null: '',
    passport: 'aa99999999999',
    percentage: '99',
    phone: '(99) 9999-9999',
    process: '99.99999999-99',
    rg: '99.999.999-9'
  }

  const types = {
    alfanumeric: '1',
    all: 'z',
    cleanText: 'c',
    email: 'e',
    letter: 'a',
    number: '9',
    qtd: '9'
  }

  const formatChars = {
    '1': '[a-zA-Z0-9 \u00C0-\u024F\u1E00-\u1EFF]',
    '9': '[0-9]',
    a: '[A-Za-z  \u00C0-\u024F\u1E00-\u1EFF]',
    c: '[a-zA-Z0-9 ,.\u00C0-\u024F\u1E00-\u1EFF]',
    e: '([A-Za-z0-9-_@.]+)',
    z: '(.*?)'
  }

  const formatQtd = (newState: any, oldState: any, _: any) => {
    const { value } = newState
    if (value.charAt(0) === '0' && value?.length > 1) {
      return {
        selection: { end: value?.length, start: value?.length },
        value: oldState.value
      }
    }
    return { selection: newState.selection, value }
  }

  const getMask = () => {
    if (mask) return mask
    if (masks) return mascaras[masks]
    return types[type].repeat(maxLength)
  }

  return (
    <Col
      marginBottom={marginBottom}
      marginRight={marginRight}
      marginTop={marginTop}
      marginLeft={marginLeft}
    >
      <InputLabelCustom hasError={hasError}>{formatLabel()}</InputLabelCustom>
      {control ? (
        <Controller
          name={formValue}
          defaultValue={defaultValue}
          control={control}
          rules={{ required }}
          render={({ field }) => (
            <StyledMaskInput
              mask={getMask()}
              value={field.value}
              onChange={field.onChange}
              disabled={props.disabled}
              maskChar={masks ? maskChar : null}
              formatChars={formatChars}
              onBlur={onBlur}
              size={900}
              width={width}
              beforeMaskedValueChange={type === 'qtd' ? formatQtd : null}
            >
              {(maskProps) => (
                <InputCustom
                  {...props}
                  {...maskProps}
                  autoComplete="off"
                  error={hasError}
                  variant="standard"
                  hasError={hasError}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        aria-label="toggle-icon-input"
                        onClick={onClickIcon}
                        style={{ margin: 0, padding: 0 }}
                      >
                        {inputIcon}
                      </IconButton>
                    )
                  }}
                />
              )}
            </StyledMaskInput>
          )}
        />
      ) : (
        <StyledMaskInput
          mask={getMask()}
          value={value}
          onChange={onChange}
          name={formValue}
          disabled={props.disabled}
          autoComplete="off"
          maskChar={masks ? maskChar : null}
          formatChars={formatChars}
          onBlur={onBlur}
          beforeMaskedValueChange={type === 'qtd' ? formatQtd : null}
        >
          {(maskProps) => (
            <InputCustom
              {...props}
              {...maskProps}
              error={hasError}
              variant="standard"
              hasError={hasError}
              InputProps={{
                endAdornment: (
                  <IconButton
                    aria-label="toggle-icon-input"
                    onClick={onClickIcon}
                    style={{ margin: 0, padding: 0 }}
                  >
                    {inputIcon}
                  </IconButton>
                )
              }}
            />
          )}
        </StyledMaskInput>
      )}
      {!!hasError && (
        <Span
          text={
            errors
              ? errors[formValue]?.message
              : customError || 'Campo obrigatório'
          }
          color={COLORS.error}
          marginTop={SPACING.spacing5}
        />
      )}
    </Col>
  )
}

export default InputMask
